// Toolbar de los catálogos de productos de V2: buscador por nombre + selector
// de orden ("Últimos subidos" / "Últimos utilizados"). Lo usan
// `PickProductPanel` y el `ProductFullMenu` del asistente; la lógica de
// filtrado/orden vive en `productCatalogFilter.ts`.
//
// Es sticky al tope del contenedor scrolleable del picker (cada panel tiene su
// propio `overflow-y-auto`, así que `top-0` funciona sin el problema del scroll
// dual de la app).
//
// El padding lo pasa el PADRE por `className`, y no es cosmético:
//   • horizontal (`-mx-N px-N`) para que el fondo blanco llegue a los bordes y
//     tape las cards al scrollear de punta a punta;
//   • superior (`pt-N`) porque el contenedor scrolleable NO debe tener
//     `padding-top`. Si lo tiene, la barra se ancla por debajo de ese padding y
//     queda una franja donde se ven pasar las cards por arriba. El espacio de
//     arriba tiene que vivir DENTRO de esta caja (que es blanca y tapa) o en
//     los bloques que van antes.

import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpDown, Check, Search, X } from 'lucide-react';
import {
  PRODUCT_SORT_LABELS,
  type ProductCatalogSort,
} from '../productCatalogFilter';

// OJO con los colores: los `brand-*` del config son `var(--brand-*)` sin
// `<alpha-value>`, así que cualquier clase con modificador de opacidad
// (`bg-brand-primary/10`, `text-brand-black/80`) NO genera regla CSS. Acá se
// usa el hex arbitrario literal — `#1644EB` = brand-primary, `#02062B` =
// brand-black — que sí compila. Literal y no una constante: el JIT de Tailwind
// escanea el texto del archivo, no evalúa interpolaciones.

const SORT_OPTIONS: ProductCatalogSort[] = ['recent', 'used'];

export interface ProductCatalogToolbarProps {
  query: string;
  onQueryChange: (q: string) => void;
  sort: ProductCatalogSort;
  onSortChange: (s: ProductCatalogSort) => void;
  /** Se agrega al contenedor sticky — el padre pasa acá su padding (ver arriba). */
  className?: string;
  /** id único del input (para el <label> accesible). */
  inputId?: string;
}

/**
 * El botón "Ordenar por" con su menú (Últimos subidos / Últimos utilizados). Es una pieza
 * aparte del toolbar para que el selector de fuentes del Agente ("Elegí qué agregar")
 * ofrezca el MISMO orden en Productos con su propio buscador
 * (ADR-2026-09-25-el-selector-de-fuentes-toma-la-biblioteca). El toolbar lo sigue
 * dibujando igual que antes.
 */
export const SelectorDeOrden: React.FC<{
  sort: ProductCatalogSort;
  onSortChange: (s: ProductCatalogSort) => void;
}> = ({ sort, onSortChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement>(null);

  // Cerrar el menú al tocar afuera o con Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!menuWrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <div className="relative shrink-0" ref={menuWrapRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={`Orden: ${PRODUCT_SORT_LABELS[sort]}`}
        className={`inline-flex items-center gap-1.5 py-2.5 px-3 rounded-xl border text-[13px] font-medium transition-colors ${
          menuOpen
            ? 'border-brand-primary text-brand-primary bg-[#1644EB]/[0.06]'
            : 'border-neutral-200 text-[#02062B]/75 hover:border-[#1644EB]/40'
        }`}
      >
        <ArrowUpDown className="w-3.5 h-3.5 shrink-0" />
        {/* Label fijo: cuál está activo se ve en el menú (con el check) y en
            el `title`. El nombre del criterio no entra al lado del input en
            el panel lateral de desktop ni en el sheet mobile. */}
        <span className="whitespace-nowrap">Ordenar por</span>
      </button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-52 rounded-2xl border border-black/[0.07] bg-white shadow-lg p-1.5 z-30"
        >
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              role="menuitemradio"
              aria-checked={sort === opt}
              onClick={() => {
                onSortChange(opt);
                setMenuOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-2 py-2 px-3 rounded-xl text-left text-[13.5px] transition-colors ${
                sort === opt
                  ? 'text-brand-primary font-semibold bg-[#1644EB]/[0.06]'
                  : 'text-[#02062B]/80 hover:bg-black/[0.04]'
              }`}
            >
              {PRODUCT_SORT_LABELS[opt]}
              {sort === opt && <Check className="w-4 h-4 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProductCatalogToolbar: React.FC<ProductCatalogToolbarProps> = ({
  query,
  onQueryChange,
  sort,
  onSortChange,
  className = '',
  inputId = 'v2-product-catalog-search',
}) => {
  return (
    <div
      // Marca para poder MEDIRLO desde afuera: la tira vertical de elegidos se alinea con
      // la primera fila de productos, o sea justo debajo de esta barra
      // (ADR-2026-09-04-la-tira-de-elegidos-va-a-la-derecha). Un `data-` y no una clase:
      // es un punto de anclaje, no un estilo.
      data-toolbar-productos=""
      className={`sticky top-0 z-20 bg-white pb-3 flex items-center gap-2 ${className}`}
    >
      <div className="relative flex-1 min-w-0">
        <label htmlFor={inputId} className="sr-only">
          Buscar productos
        </label>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
        <input
          id={inputId}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Buscar producto..."
          // `text-[16px]` en el input: menos de 16px hace que iOS zoomee la
          // pantalla al enfocarlo, y en el bottom sheet queda pésimo.
          className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-neutral-200 bg-white text-[16px] sm:text-[14px] text-[#02062B] placeholder:text-neutral-400 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            aria-label="Limpiar búsqueda"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-neutral-400 hover:text-brand-black hover:bg-black/[0.05] transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <SelectorDeOrden sort={sort} onSortChange={onSortChange} />
    </div>
  );
};

/** Estado vacío cuando la búsqueda no matchea ningún producto. */
export const ProductCatalogNoResults: React.FC<{
  query: string;
  onClear: () => void;
}> = ({ query, onClear }) => (
  <div className="flex flex-col items-center justify-center text-center py-12 px-4">
    <div className="w-11 h-11 rounded-full bg-black/[0.04] flex items-center justify-center mb-3">
      <Search className="w-5 h-5 text-neutral-400" />
    </div>
    <p className="text-[14px] font-medium text-[#02062B]/80">
      Sin resultados para “{query.trim()}”
    </p>
    <p className="text-[12.5px] text-[#3C3C43]/55 mt-1">
      Probá con otro nombre.
    </p>
    <button
      type="button"
      onClick={onClear}
      className="mt-3 text-[13px] font-medium text-brand-primary hover:opacity-75 transition-opacity"
    >
      Limpiar búsqueda
    </button>
  </div>
);
